import {
  Button,
  Col,
  Modal,
  Divider,
  Dropdown,
  Empty,
  Flex,
  InputNumber,
  message,
  Row,
  Select,
  Tag,
  Typography,
  Alert,
} from "antd";
import { ApiService } from "api/api.service";
import { SelectStrategyParams } from "api/strategy.service";
import PageTitle from "components/PageTitle";
import { loader } from "components/QueryWrapper";
import { GuardRails, ResolutionType, StrategyFormat } from "dtos/strategy.dto";
import DashboardLayout from "layout/DashboardLayout";
import { ReactNode, useEffect, useState } from "react";
import { LuDollarSign, LuHandshake, LuRocket } from "react-icons/lu";
import {
  TbBan,
  TbBuilding,
  TbCalculator,
  TbCalendarMonth,
  TbChessKnight,
  TbChevronDown,
  TbInfoCircle,
  TbPlus,
  TbRefresh,
  TbTax,
  TbX,
} from "react-icons/tb";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { formatMoney, formatNumber, ObjectId } from "utils/functions";
import { BsBank } from "react-icons/bs";
import { ColorBox } from "components/ColorBox";
import CustomModal from "components/CustomStrategy/CustomModal";
import SavedStrategiesModal from "components/CustomStrategyV2/SavedStrategiesModal";
import StrategyWizardModal from "components/CustomStrategyV2/StrategyWizardModal";
import { OrgSelector } from "components/Organisation/OrganisationSelector";
import { OrganisationDto } from "dtos/organisation.dto";
import { StrategyCard } from "./ViewStrategy";
import { PreviewStrategyModal } from "./PreviewStrategy";
import { SelectorKey } from "utils/core/strategy.util";

const TextWithFallback = ({
  children,
  fallback = "-",
  className,
}: {
  children?: ReactNode;
  fallback?: string;
  className?: string;
}) => children ?? <span className={`text-gray-500 ${className}`}>{fallback}</span>;

type GetStrategyParams = Partial<{
  total_weeks: number;
  weekly_budget: number;
  format: StrategyFormat;
  type: ResolutionType;
}>;

interface CustomParamSelectorProps<M = "total_weeks" | "weekly_budget" | "none"> {
  mode: M;
  setMode: (val: M) => unknown;
  // totalWeeks?: number;
  // weeklyBudget?: number;
  setField: (field: M, value: number | undefined) => unknown;
  onFinish: () => unknown;
  failed?: {
    slug: string;
    error: unknown;
    guardRails: Partial<GuardRails>;
  }[];
  success: number;
  applied: Partial<Record<"totalWeeks" | "weeklyBudget", number>>;
  current: Partial<Record<"totalWeeks" | "weeklyBudget", number>>;
  onClear: () => void;
}

const CustomParamSelector = ({
  mode,
  setMode,
  applied,
  current,
  setField,
  onFinish,
  failed,
  success,
  onClear,
}: CustomParamSelectorProps) => {
  const [minBudget, setMinBudget] = useState<number | undefined>();
  const [maxWeeks, setMaxWeeks] = useState<number | undefined>();

  useEffect(() => {
    type Range = Record<"min" | "max", number | undefined>;
    const minWeeklyBudget: Range = { min: undefined, max: undefined };
    const maxTotalWeeks: Range = { min: undefined, max: undefined };

    if (failed) {
      for (const f of failed) {
        if (f.guardRails.min_custom_amount !== undefined) {
          minWeeklyBudget.min ??= f.guardRails.min_custom_amount;
          minWeeklyBudget.min = Math.min(f.guardRails.min_custom_amount, minWeeklyBudget.min);
          minWeeklyBudget.max ??= f.guardRails.min_custom_amount;
          minWeeklyBudget.max = Math.max(f.guardRails.min_custom_amount, minWeeklyBudget.max);
        }
        if (f.guardRails.max_custom_weeks !== undefined) {
          maxTotalWeeks.min ??= f.guardRails.max_custom_weeks;
          maxTotalWeeks.min = Math.min(f.guardRails.max_custom_weeks, maxTotalWeeks.min);
          maxTotalWeeks.max ??= f.guardRails.max_custom_weeks;
          maxTotalWeeks.max = Math.max(f.guardRails.max_custom_weeks, maxTotalWeeks.max);
        }
      }
    }

    if (minWeeklyBudget.min) setMinBudget(minWeeklyBudget.min);
    if (maxTotalWeeks.max) setMaxWeeks(maxTotalWeeks.max);

    if (!success) {
      if (applied.weeklyBudget && minWeeklyBudget.min && applied.weeklyBudget < minWeeklyBudget.min) {
        message.warning(`Weekly Budget can not be lower than ${formatMoney(minWeeklyBudget.min)}`);
      } else if (applied.totalWeeks && maxTotalWeeks.max && applied.totalWeeks > maxTotalWeeks.max) {
        message.warning(`Total weeks can not be over ${maxTotalWeeks.max}`);
      }
    }
  }, [failed]);

  const handleClear = () => {
    setField("total_weeks", undefined);
    setField("weekly_budget", undefined);
    setMode("none");
    onClear();
  };

  return (
    <div className="fixed bottom-6 left-0 w-full z-50 px-4">
      <div
        className="max-w-md mx-auto
          bg-white/80 backdrop-blur-xl
          border border-solid border-gray-200
          shadow-lg rounded-full px-3 py-2"
      >
        <div className="flex items-center gap-2">
          {/* Two mode toggle buttons side by side */}
          <div className="flex gap-1">
            <button
              onClick={() => setMode("weekly_budget")}
              className={`flex items-center gap-2 p-1 px-2 text-sm font-medium rounded-full transition border
                ${
                  mode === "weekly_budget"
                    ? "bg-yellow-50 text-yellow-700 border-yellow-400"
                    : "bg-transparent text-gray-400 border-transparent"
                }`}
            >
              <ColorBox roundness="full" size="small" color={"yellow"}>
                <TbCalculator size={16} />
              </ColorBox>
              Budget
            </button>

            <button
              onClick={() => setMode("total_weeks")}
              className={`flex items-center gap-2 p-1 px-2 text-sm font-medium rounded-full transition border
                ${
                  mode === "total_weeks"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-400"
                    : "bg-transparent text-gray-400 border-transparent"
                }`}
            >
              <ColorBox roundness="full" size="small" color={"emerald"}>
                <TbCalendarMonth size={16} />
              </ColorBox>
              Weeks
            </button>
          </div>

          {/* Input area */}
          {mode === "none" && (
            <InputNumber
              readOnly
              variant="borderless"
              placeholder="View fixed plans"
              className="flex-1 text-sm font-medium"
            />
          )}
          {mode === "total_weeks" && (
            <InputNumber
              value={current.totalWeeks}
              max={maxWeeks}
              onChange={(val) => {
                setField("weekly_budget", undefined);
                setField("total_weeks", val ?? undefined);
              }}
              controls={false}
              variant="borderless"
              placeholder="Enter weeks"
              className="flex-1 text-sm font-medium"
            />
          )}
          {mode === "weekly_budget" && (
            <InputNumber
              value={current.weeklyBudget}
              min={minBudget}
              prefix="$"
              onChange={(val) => {
                setField("weekly_budget", val ?? undefined);
                setField("total_weeks", undefined);
              }}
              controls={false}
              variant="borderless"
              placeholder="Enter budget"
              className="flex-1 text-sm font-medium"
            />
          )}

          {mode !== "none" && (
            <Button
              onClick={handleClear}
              shape="circle"
              size="small"
              icon={<TbX size={14} />}
              className="border border-gray-200 text-gray-400 hover:text-gray-600"
            />
          )}

          <Button className="rounded-full self-end px-3" icon={<LuRocket className="size-4" />} onClick={onFinish}>
            Go
          </Button>
        </div>
      </div>
    </div>
  );
};
const SelectStrategy = () => {
  const { appkey: appKey } = useParams();
  const navigate = useNavigate();
  const defaultCustomParams: GetStrategyParams = {
    type: ResolutionType.NEGOTIATE,
    format: StrategyFormat.DEFAULT,
    total_weeks: undefined,
    weekly_budget: undefined,
  };
  const [sp, setSp] = useSearchParams();
  const [customParams, setCustomParams] = useState<GetStrategyParams>(defaultCustomParams);
  const [strategyParams, setStrategyParams] = useState<GetStrategyParams>(defaultCustomParams);
  const [orgCode, setOrgCode] = useState<string>();
  const [previewSlug, setPreviewSlug] = useState<string | undefined>();
  const [customModalOpen, setCustomModalOpen] = useState<"v1" | "v2" | undefined>(undefined);
  const [strategyWizard, setStrategyWizard] = useState<{ basePlanSlug?: string; editIndex?: number }>();
  const [savedFocusIndex, setSavedFocusIndex] = useState<number>();
  const [confirmSlug, setConfirmSlug] = useState<string | undefined>();

  const organisationQuery = ApiService.organisation.useGetAllOrganisations();
  const strategiesQuery = ApiService.strategy.useGetStrategies({ appKey, orgCode, ...strategyParams });
  const strategies = strategiesQuery.data?.result?.strategies ?? [];
  const selectStrategy = ApiService.strategy.useSelectStrategy();
  const selectedStrategySlug = strategiesQuery.data?.result.selected;
  const applicationQuery = ApiService.application.useGetByAppKey(appKey);
  const adminApplication = applicationQuery.data?.application;

  const clientName = applicationQuery.data?.application.meta.client_name ?? "Application Item";
  const validStrategies = strategies.filter((strategy) => strategy.slug);
  const businessInfo = strategiesQuery.data?.result.meta;

  const getSupportedResolutionTypes = (org: OrganisationDto) => {
    return new Set((org.strategies ?? []).map((str) => str.resolution_type));
  };

  const cachedOrg = sp.get("orgCode");
  useEffect(() => {
    if (!organisationQuery.data || (!applicationQuery.data && !cachedOrg)) return;

    let org: OrganisationDto | undefined;
    if (cachedOrg) {
      org = organisationQuery.data.find((o) => o.code === cachedOrg);
    } else if (applicationQuery.data?.application.organisation) {
      const orgId = applicationQuery.data?.application.organisation;
      org = organisationQuery.data.find((o) => orgId && new ObjectId(orgId).equals(o._id));
    }

    org ??= organisationQuery.data.find((o) => o.code.toLocaleLowerCase() === "default");
    if (!org?.code) return;

    const availableTypes = getSupportedResolutionTypes(org);
    const firstType = availableTypes.values().next().value;

    setOrgCode(org.code);
    setStrategyParams((prev) => {
      if (!prev.type || !availableTypes.has(prev.type)) {
        return { ...prev, type: firstType ?? defaultCustomParams.type };
      }
      return prev;
    });
  }, [organisationQuery.data, applicationQuery.data, orgCode]);

  const redirectStage =
    applicationQuery.data?.data.application.status.signatures === true || applicationQuery.data?.application.plan?.key
      ? "ledger"
      : "agreements";

  const onSelect = (payload: SelectStrategyParams) => {
    if (!payload.appKey || !payload.slug) {
      message.error(`${payload.appKey ? "Strategy Slug" : "AppKey"} is missing.`);
      return;
    }

    const redirect = () => navigate(`/dashboard/application/${payload.appKey}?tab=${redirectStage}`);

    const selector = new SelectorKey(payload.slug);
    if (selector.compareId(selectedStrategySlug)) {
      return redirect();
    } else {
      selectStrategy.mutate(payload, {
        onSuccess() {
          redirect();
        },
      });
    }
  };

  const onReExecute = (payload: SelectStrategyParams) => {
    if (!payload.appKey || !payload.slug) {
      return message.error(`${payload.appKey ? "Strategy Slug" : "AppKey"} is missing.`);
    }

    const selector = new SelectorKey(payload.slug);
    if (!selector.compareId(payload.slug)) {
      return message.error("Strategy is not selected");
    }

    const redirect = () => navigate(`/dashboard/application/${payload.appKey}?tab=ledger`);
    selectStrategy.mutate(payload, {
      onSuccess() {
        redirect();
      },
    });
  };

  const applyFilters = () => {
    const format =
      !customParams.total_weeks && !customParams.weekly_budget ? StrategyFormat.DEFAULT : StrategyFormat.CUSTOM;
    setStrategyParams(() => ({ ...customParams, type: strategyParams.type, format }));
  };
  const resetFilters = () => {
    const org = organisationQuery.data?.find((org) => org.code === orgCode);
    const availableStrategyTypes = org ? getSupportedResolutionTypes(org) : new Set();
    const firstType = availableStrategyTypes.values().next().value as ResolutionType | undefined;

    const resetParams: GetStrategyParams = { ...defaultCustomParams, type: firstType ?? defaultCustomParams.type };
    setCustomParams(resetParams);
    setStrategyParams(resetParams);
  };
  const [mode, setMode] = useState<CustomParamSelectorProps["mode"] | "none">("none");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  return (
    <DashboardLayout
      headerRight={
        <Flex className="items-center gap-2 h-[56px] w-full">
          <Select
            className="w-40 h-9"
            value={strategyParams.type}
            onChange={(val) => setStrategyParams((prev) => ({ ...prev, type: val }))}
            options={[
              {
                key: ResolutionType.NEGOTIATE,
                value: ResolutionType.NEGOTIATE,
                label: (
                  <Typography.Text>
                    <LuHandshake size={16} className="mr-2 inline-flex" />
                    Negotiate
                  </Typography.Text>
                ),
              },
              {
                key: ResolutionType.SETTLEMENT,
                value: ResolutionType.SETTLEMENT,
                label: (
                  <Typography.Text>
                    <LuDollarSign size={16} className="mr-2 inline-flex" />
                    Settlement
                  </Typography.Text>
                ),
              },
            ]}
          />
          <OrgSelector
            checkDisabled={(org) => org.isReady !== true}
            organisations={organisationQuery.data ?? []}
            selected={orgCode}
            onChange={(org) => {
              if (org.isReady) {
                setOrgCode(org.code);
                setSp((prev) => ({ ...prev, orgCode: org.code }));

                const availableTypes = getSupportedResolutionTypes(org);
                const firstType = availableTypes.values().next().value;

                setStrategyParams((prev) => {
                  if (!prev.type || !availableTypes.has(prev.type)) {
                    return { ...prev, type: firstType ?? defaultCustomParams.type };
                  }
                  return prev;
                });
              } else {
                message.warning("Organisation is not setup yet, can not view strategies");
              }
            }}
            extra={(org) =>
              org.isReady && !org.disabled ? <Tag className="ml-auto">{org.strategies?.length}</Tag> : null
            }
          />
        </Flex>
      }
    >
      <PageTitle
        title="Relief Strategies"
        pageTitle={[
          { title: "Applications", link: `/dashboard/applications` },
          {
            title: clientName || appKey || "",
            link: `/dashboard/application/${appKey}`,
          },
          { title: "Strategies" },
        ]}
      />
      {!!adminApplication?.plan?.key && adminApplication?.isPriorApplication && (
        <div className="mx-4 mt-2">
          <Alert
            icon={<TbInfoCircle size={16} />}
            type="info"
            closable
            message="This is a prior application and strategy can't be selected anymore"
          />
        </div>
      )}

      <Flex className="size-full">
        {strategiesQuery.data?.result.type !== StrategyFormat.MANAGERS_APPROVAL && (
          <CustomParamSelector
            mode={mode}
            setMode={setMode}
            current={{
              totalWeeks: customParams.total_weeks,
              weeklyBudget: customParams.weekly_budget,
            }}
            applied={{
              totalWeeks: strategyParams.total_weeks,
              weeklyBudget: strategyParams.weekly_budget,
            }}
            failed={strategiesQuery.data?.result.non_eligible}
            success={strategiesQuery.data?.result.strategies.length ?? 0}
            setField={(key, val) => {
              setCustomParams((prev) => {
                return {
                  ...prev,
                  [key]: val,
                };
              });
            }}
            onClear={() => {
              setMode("none");

              const clearedParams = {
                total_weeks: undefined,
                weekly_budget: undefined,
              };
              setCustomParams(clearedParams);

              const format = StrategyFormat.DEFAULT;
              setStrategyParams(() => ({
                ...customParams,
                type: strategyParams.type,
                total_weeks: undefined,
                weekly_budget: undefined,
                format,
              }));
            }}
            onFinish={() => {
              if (mode === "total_weeks" && !customParams.total_weeks) {
                return message.error("Enter number of weeks");
              } else if (mode === "weekly_budget" && !customParams.weekly_budget) {
                return message.error("Enter weekly budget");
              }

              applyFilters();
            }}
          />
        )}

        <div className="w-3/4 h-full min-h-fit border-0 border-r border-solid border-gray-300">
          {strategiesQuery.isLoading ? (
            <div className="h-full flex flex-col items-center justify-center w-full">{loader}</div>
          ) : validStrategies?.length ? (
            <div className="w-full mb-12">
              <Row gutter={[20, 32]} className="p-5 py-8">
                {validStrategies.map((strategy) => (
                  <Col key={strategy.slug} xs={24} sm={12} xl={8} xxl={6}>
                    <StrategyCard
                      strategy={strategy}
                      selected={selectedStrategySlug}
                      redirectStage={redirectStage}
                      isLoading={
                        selectStrategy.isPending &&
                        new SelectorKey(strategy.selector_key).compareId(selectStrategy.variables.slug)
                      }
                      onSelect={({ isSelected }) => {
                        const slug = strategy.selector_key || strategy.slug;
                        if (isSelected) {
                          onSelect({ appKey, slug });
                        } else {
                          setConfirmSlug(slug);
                          setIsConfirmModalOpen(true);
                        }
                      }}
                      onPreview={() => {
                        setPreviewSlug(strategy.selector_key || strategy.slug);
                        setIsPreviewModalOpen(true);
                      }}
                      onCustomize={() => setStrategyWizard({ basePlanSlug: strategy.slug })}
                      onReExecute={() => {
                        onReExecute({ appKey, slug: strategy.selector_key || strategy.slug });
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_DEFAULT}
              description={"No strategies found for your given config"}
              className="h-full flex flex-col items-center justify-center w-full"
            >
              <Button onClick={resetFilters} variant="outlined" color="purple" icon={<TbRefresh />}>
                Reset Filters
              </Button>
            </Empty>
          )}
        </div>

        <div className="sticky top-0 self-start h-fit w-1/4 min-w-20">
          <div className="px-4 pt-4">
            <div className="border border-b-0 border-solid border-gray-200 p-1 rounded-t-md text-center">
              <Typography.Title level={5} style={{ margin: 0 }} className="py-2 !text-purple-600">
                Business Information
              </Typography.Title>
            </div>
            <Flex vertical className="border border-solid gap-3 border-gray-200 p-2 rounded-md rounded-t-none">
              <div className="flex flex-col items-start gap-1">
                <Typography.Paragraph type="secondary" style={{ margin: 0 }} className="!text-sm font-semibold ">
                  <span className="flex items-center gap-1">
                    <div className="size-6 rounded-full bg-purple-100 flex items-center justify-center">
                      <TbBuilding className="stroke-purple-600" size={16} />
                    </div>
                    Company Name
                  </span>
                </Typography.Paragraph>
                <TextWithFallback className="flex justify-end mt-2">
                  {businessInfo?.business_name ? (
                    <span className="text-gray-700 font-semibold ml-6 leading-tight">{businessInfo.business_name}</span>
                  ) : null}
                </TextWithFallback>
              </div>
              <div className="flex items-center">
                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: 0, width: 180 }}
                  className="!text-sm font-semibold"
                >
                  <span className="flex items-center gap-1">
                    <div className="size-6 rounded-full bg-yellow-100 flex items-center justify-center">
                      <LuDollarSign className="stroke-yellow-600" size={16} />
                    </div>
                    Total Debt :
                  </span>
                </Typography.Paragraph>
                <TextWithFallback>
                  {businessInfo?.total_debt === undefined ? null : (
                    <span className="text-gray-700 font-bold">{formatNumber(businessInfo.total_debt)}</span>
                  )}
                </TextWithFallback>
              </div>
              <div className="flex items-center">
                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: 0, width: 180 }}
                  className="!text-sm font-semibold"
                >
                  <span className="flex items-center gap-1">
                    <div className="size-6 rounded-full bg-indigo-100 flex items-center justify-center">
                      <BsBank className="text-indigo-600" size={16} />
                    </div>
                    Active Loans :
                  </span>
                </Typography.Paragraph>
                <TextWithFallback>
                  {businessInfo?.active_loans === undefined ? null : (
                    <span className="text-gray-700 font-bold">{businessInfo.active_loans}</span>
                  )}
                </TextWithFallback>
              </div>
              <div className="flex items-center">
                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: 0, width: 180 }}
                  className="!text-sm font-semibold"
                >
                  <span className="flex items-center gap-1">
                    <div className="size-6 rounded-full bg-rose-100 flex items-center justify-center">
                      <TbTax className="stroke-rose-600" size={16} />
                    </div>
                    Active Lawsuits :
                  </span>
                </Typography.Paragraph>
                <TextWithFallback>
                  {businessInfo?.active_lawsuits === undefined ? null : (
                    <span className="text-gray-700 font-bold">{businessInfo.active_lawsuits}</span>
                  )}
                </TextWithFallback>
              </div>
            </Flex>
          </div>
          <Divider />
          <div className="px-4">
            <Dropdown
              menu={{
                items: [
                  {
                    key: "v1",
                    label: (
                      <Flex className="gap-2 justify-between">
                        <span>View v1 Strategies</span>
                        <Tag color="blue" className="h-5 uppercase">
                          Deprecated
                        </Tag>
                      </Flex>
                    ),
                    onClick: () => setCustomModalOpen("v1"),
                  },
                  {
                    key: "v2",
                    label: "View v2 Strategies",
                    onClick: () => {
                      setSavedFocusIndex(undefined);
                      setCustomModalOpen("v2");
                    },
                  },
                  {
                    key: "v2-new",
                    label: (
                      <div>
                        <Button block variant="solid" color="magenta" icon={<TbPlus />}>
                          Add new strategy
                        </Button>
                      </div>
                    ),
                    onClick: () => setStrategyWizard({}),
                  },
                ],
              }}
            >
              <Button
                icon={<TbChessKnight size={20} />}
                type="primary"
                variant="solid"
                color="geekblue"
                className="w-full"
              >
                Customize Plan
              </Button>
            </Dropdown>
          </div>
        </div>
      </Flex>
      <CustomModal isOpen={customModalOpen === "v1"} onClose={() => setCustomModalOpen(undefined)} appkey={appKey} />
      <SavedStrategiesModal
        appkey={appKey}
        open={customModalOpen === "v2"}
        selected={selectedStrategySlug}
        focusIndex={savedFocusIndex}
        onClose={() => setCustomModalOpen(undefined)}
        onAdd={() => setStrategyWizard({})}
        onEdit={(editIndex) => setStrategyWizard({ editIndex })}
      />
      <StrategyWizardModal
        appkey={appKey}
        orgCode={orgCode || "fcds25"}
        open={Boolean(strategyWizard)}
        basePlanSlug={strategyWizard?.basePlanSlug}
        editIndex={strategyWizard?.editIndex}
        onClose={() => setStrategyWizard(undefined)}
        onSaved={(index) => {
          setSavedFocusIndex(index);
          setCustomModalOpen("v2");
        }}
      />
      <PreviewStrategyModal
        open={isPreviewModalOpen}
        appKey={appKey}
        selector_key={previewSlug}
        onConfirm={onSelect}
        onCancel={() => {
          setPreviewSlug(undefined);
          setIsPreviewModalOpen(false);
        }}
        okText={
          previewSlug && new SelectorKey(previewSlug).compareId(selectedStrategySlug)
            ? `View ${redirectStage}`
            : undefined
        }
      />
      <ConfirmStrategyModal
        visible={isConfirmModalOpen}
        onConfirm={onSelect}
        onCancel={() => {
          setIsConfirmModalOpen(false);
          setConfirmSlug(undefined);
        }}
        appKey={appKey as string}
        selectorKey={confirmSlug}
      />
    </DashboardLayout>
  );
};

export default SelectStrategy;

const ConfirmStrategyModal = ({
  visible,
  onConfirm,
  onCancel,
  appKey,
  selectorKey,
}: {
  visible: boolean;
  onConfirm: (payload: SelectStrategyParams) => unknown;
  onCancel: () => void;
  appKey: string;
  selectorKey: string | undefined;
}) => {
  return (
    <Modal
      title="Confirm Selection"
      open={visible}
      onOk={() => onConfirm({ appKey, slug: selectorKey })}
      onCancel={onCancel}
      okText="Yes, select this"
    >
      <Typography.Text>Are you sure you want to select this strategy?</Typography.Text>
    </Modal>
  );
};
